import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FiEye, FiUsers, FiMousePointer, FiBarChart2,
  FiTrendingUp, FiActivity, FiEdit3, FiMonitor,
  FiSmartphone, FiTablet, FiTrash2, FiRefreshCw,
} from 'react-icons/fi';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { apiGetAnalyticsSummary, apiClearAnalytics } from '../../api/analyticsApi';
import { useAuth } from '../../context/AuthContext';
import GoogleSignInGate from '../../components/GoogleSignInGate';
import UserBadge from '../../components/UserBadge';
import ConfirmModal from '../../components/ConfirmModal';

const ACCENT = '#6cb4ff';
const GREEN = '#34d399';
const PURPLE = '#a78bfa';
const AMBER = '#fbbf24';
const COLORS = [ACCENT, PURPLE, GREEN, AMBER, '#f87171', '#22d3ee', '#818cf8', '#fb923c'];

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ad-tooltip">
      <p className="ad-tooltip-label">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>{p.name}: <strong>{p.value}</strong></p>
      ))}
    </div>
  );
};

function heatColor(ratio) {
  // 0 = cool blue, 1 = hot red
  if (ratio > 0.75) return '#ef4444';
  if (ratio > 0.50) return '#f59e0b';
  if (ratio > 0.25) return '#34d399';
  return '#6cb4ff';
}

// eslint-disable-next-line no-unused-vars
function deviceIcon(device) {
  if (device.includes('Mobile')) return <FiSmartphone />;
  if (device.includes('Tablet')) return <FiTablet />;
  return <FiMonitor />;
}

export default function UsageDashboard() {
  useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('overview'); // overview | heatmap
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = () => {
    setLoading(true);
    apiGetAnalyticsSummary().then((res) => {
      if (res.ok) setData(res.data);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const handleClear = async () => {
    setClearing(true);
    await apiClearAnalytics();
    setData(null);
    setClearing(false);
    setShowClearModal(false);
    load();
  };

  /* ── Derived data ── */
  const dailyChartData = useMemo(() => {
    if (!data?.dailyViews?.length) return [];
    return data.dailyViews.map((d) => ({
      ...d,
      name: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    }));
  }, [data]);

  const topPages = data?.topPages || [];
  const topReferrers = data?.topReferrers || [];
  const deviceBreakdown = data?.deviceBreakdown || [];
  const avgScroll = data?.avgScrollDepth || {};
  const maxPageViews = topPages.length > 0 ? topPages[0].views : 1;

  return (
    <GoogleSignInGate>
      <div className="admin-page">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-header-inner">
            <Link to="/" className="research-brand">
              <span className="research-brand-text"><strong>Vector Labs</strong> Admin</span>
            </Link>
            <nav className="admin-nav">
              <Link to="/admin/writing" className="admin-nav-link">
                <FiEdit3 style={{ marginRight: 4 }} />Writing
              </Link>
              <Link to="/admin/usage" className="admin-nav-link active">
                <FiActivity style={{ marginRight: 4 }} />Usage
              </Link>
              <Link to="/" className="admin-nav-link">Home</Link>
            </nav>
            <UserBadge />
          </div>
        </div>

        <div className="admin-body">
          {/* Page header */}
          <div className="ad-page-header">
            <div className="ad-page-title">
              <FiEye className="ad-title-icon" style={{ color: GREEN }} />
              <div>
                <h1>Website Usage & Heatmap</h1>
                <p>Real-time page views, traffic sources, device breakdown, and engagement heatmap.</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="admin-btn secondary" onClick={load}>
                <FiRefreshCw /> Refresh
              </button>
              <button className="admin-btn secondary" style={{ color: '#f87171' }} onClick={() => setShowClearModal(true)}>
                <FiTrash2 /> Clear Data
              </button>
            </div>
          </div>

          {/* View toggle */}
          <div className="usage-toggle-row">
            {[
              { key: 'overview', label: 'Overview', icon: <FiBarChart2 /> },
              { key: 'heatmap', label: 'Heatmap', icon: <FiMousePointer /> },
            ].map((v) => (
              <button
                key={v.key}
                className={`usage-toggle-btn ${view === v.key ? 'active' : ''}`}
                onClick={() => setView(v.key)}
              >
                {v.icon} {v.label}
              </button>
            ))}
          </div>

          {loading && (
            <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '3rem' }}>
              Loading analytics data…
            </p>
          )}

          {!loading && !data && (
            <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '3rem' }}>
              No analytics data yet. Browse around the site to start collecting data.
            </p>
          )}

          {!loading && data && view === 'overview' && (
            <>
              {/* KPI cards */}
              <div className="ad-kpi-grid">
                <div className="ad-kpi-card">
                  <div className="ad-kpi-icon" style={{ background: 'rgba(108,180,255,0.12)', color: ACCENT }}><FiEye /></div>
                  <div className="ad-kpi-data">
                    <span className="ad-kpi-number">{(data.totalViews || 0).toLocaleString()}</span>
                    <span className="ad-kpi-label">Page Views</span>
                  </div>
                </div>
                <div className="ad-kpi-card">
                  <div className="ad-kpi-icon" style={{ background: 'rgba(52,211,153,0.12)', color: GREEN }}><FiUsers /></div>
                  <div className="ad-kpi-data">
                    <span className="ad-kpi-number">{(data.uniqueVisitors || 0).toLocaleString()}</span>
                    <span className="ad-kpi-label">Unique Visitors</span>
                  </div>
                </div>
                <div className="ad-kpi-card">
                  <div className="ad-kpi-icon" style={{ background: 'rgba(167,139,250,0.12)', color: PURPLE }}><FiMousePointer /></div>
                  <div className="ad-kpi-data">
                    <span className="ad-kpi-number">{(data.totalClicks || 0).toLocaleString()}</span>
                    <span className="ad-kpi-label">Tracked Clicks</span>
                  </div>
                </div>
                <div className="ad-kpi-card">
                  <div className="ad-kpi-icon" style={{ background: 'rgba(251,191,36,0.12)', color: AMBER }}><FiTrendingUp /></div>
                  <div className="ad-kpi-data">
                    <span className="ad-kpi-number">{topPages.length}</span>
                    <span className="ad-kpi-label">Active Pages</span>
                  </div>
                </div>
              </div>

              {/* Daily views chart */}
              <div className="ad-chart-row ad-chart-row--full">
                <div className="ad-chart-card">
                  <h3><FiTrendingUp /> Daily Page Views (Last 30 Days)</h3>
                  {dailyChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart data={dailyChartData} margin={{ left: 0, right: 10, top: 10, bottom: 5 }}>
                        <defs>
                          <linearGradient id="gradViews" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={ACCENT} stopOpacity={0.3} />
                            <stop offset="100%" stopColor={ACCENT} stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} tick={{ fill: 'rgba(255,255,255,0.45)' }} />
                        <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                        <Tooltip content={<Tip />} />
                        <Area type="monotone" dataKey="views" name="Views" stroke={ACCENT} fill="url(#gradViews)"
                          strokeWidth={2.5} dot={{ r: 3, fill: ACCENT }} activeDot={{ r: 5 }} animationDuration={1500} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <p style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '2rem' }}>No daily data yet.</p>
                  )}
                </div>
              </div>

              {/* Top pages + Referrers */}
              <div className="ad-chart-row ad-chart-row--1-1">
                <div className="ad-chart-card">
                  <h3><FiBarChart2 /> Top Pages</h3>
                  <table className="usage-mini-table">
                    <thead>
                      <tr><th>Path</th><th style={{ textAlign: 'right' }}>Views</th></tr>
                    </thead>
                    <tbody>
                      {topPages.slice(0, 10).map((p) => (
                        <tr key={p.path}>
                          <td><span className="usage-path">{p.path}</span></td>
                          <td style={{ textAlign: 'right' }}>{p.views}</td>
                        </tr>
                      ))}
                      {topPages.length === 0 && (
                        <tr><td colSpan={2} style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>No data</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="ad-chart-card">
                  <h3><FiActivity /> Referrers</h3>
                  <table className="usage-mini-table">
                    <thead>
                      <tr><th>Source</th><th style={{ textAlign: 'right' }}>Count</th></tr>
                    </thead>
                    <tbody>
                      {topReferrers.map((r) => (
                        <tr key={r.referrer}>
                          <td>{r.referrer}</td>
                          <td style={{ textAlign: 'right' }}>{r.count}</td>
                        </tr>
                      ))}
                      {topReferrers.length === 0 && (
                        <tr><td colSpan={2} style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>No data</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Device breakdown */}
              <div className="ad-chart-row ad-chart-row--2-1">
                <div className="ad-chart-card">
                  <h3><FiBarChart2 /> Device Breakdown</h3>
                  {deviceBreakdown.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={deviceBreakdown} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="device" stroke="#94a3b8" fontSize={11} tick={{ fill: 'rgba(255,255,255,0.45)' }} />
                        <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                        <Tooltip content={<Tip />} />
                        <Bar dataKey="count" name="Visitors" radius={[6, 6, 0, 0]} animationDuration={1000}>
                          {deviceBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '2rem' }}>No data.</p>
                  )}
                </div>

                <div className="ad-chart-card">
                  <h3><FiMonitor /> Device Mix</h3>
                  {deviceBreakdown.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={160}>
                        <PieChart>
                          <Pie data={deviceBreakdown} cx="50%" cy="50%" innerRadius={40} outerRadius={65}
                            paddingAngle={3} dataKey="count" nameKey="device" animationDuration={1000}>
                            {deviceBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip content={<Tip />} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="ad-pie-legend">
                        {deviceBreakdown.map((d, i) => (
                          <span key={d.device}>
                            <span className="ad-legend-dot" style={{ background: COLORS[i % COLORS.length] }} />
                            {d.device} ({d.count})
                          </span>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '2rem' }}>No data.</p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ═══════ HEATMAP VIEW ═══════ */}
          {!loading && data && view === 'heatmap' && (
            <>
              {/* Page engagement heatmap (bar-based) */}
              <div className="ad-chart-row ad-chart-row--full">
                <div className="ad-chart-card">
                  <h3><FiMousePointer /> Page Engagement Heatmap</h3>
                  <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', marginBottom: 16, marginTop: -8 }}>
                    Bar width = relative page views. Color intensity = engagement level.
                  </p>
                  <div className="heatmap-container">
                    {topPages.slice(0, 15).map((page) => {
                      const ratio = page.views / maxPageViews;
                      const scroll = avgScroll[page.path] || 0;
                      return (
                        <div key={page.path} className="heatmap-page-row">
                          <span className="heatmap-page-label" title={page.path}>{page.path}</span>
                          <div className="heatmap-bar-track">
                            <div
                              className="heatmap-bar-fill"
                              style={{
                                width: `${Math.max(ratio * 100, 8)}%`,
                                background: `linear-gradient(90deg, ${heatColor(ratio)}, ${heatColor(ratio)}dd)`,
                              }}
                            >
                              {page.views} views{scroll > 0 ? ` · ${scroll}% scroll` : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {topPages.length === 0 && (
                      <p style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '2rem' }}>
                        No heatmap data yet. Browse the site to collect data.
                      </p>
                    )}
                  </div>
                  <div className="heatmap-legend">
                    <span><span className="heatmap-legend-swatch" style={{ background: '#6cb4ff' }} /> Low</span>
                    <span><span className="heatmap-legend-swatch" style={{ background: '#34d399' }} /> Medium</span>
                    <span><span className="heatmap-legend-swatch" style={{ background: '#f59e0b' }} /> High</span>
                    <span><span className="heatmap-legend-swatch" style={{ background: '#ef4444' }} /> Hot</span>
                  </div>
                </div>
              </div>

              {/* Scroll depth by page */}
              <div className="ad-chart-row ad-chart-row--full">
                <div className="ad-chart-card">
                  <h3><FiTrendingUp /> Average Scroll Depth by Page</h3>
                  {Object.keys(avgScroll).length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart
                        data={Object.entries(avgScroll)
                          .sort(([, a], [, b]) => b - a)
                          .slice(0, 12)
                          .map(([path, depth]) => ({ path: path.length > 25 ? path.slice(0, 25) + '…' : path, depth }))}
                        margin={{ left: 10, right: 10, top: 5, bottom: 5 }}
                        layout="vertical"
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis type="number" domain={[0, 100]} stroke="#94a3b8" fontSize={12}
                          tickFormatter={(v) => `${v}%`} />
                        <YAxis type="category" dataKey="path" width={140} stroke="#94a3b8" fontSize={10}
                          tick={{ fill: 'rgba(255,255,255,0.45)' }} />
                        <Tooltip content={<Tip />} />
                        <Bar dataKey="depth" name="Scroll %" radius={[0, 6, 6, 0]} animationDuration={1000}>
                          {Object.entries(avgScroll).map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '2rem' }}>
                      No scroll data yet.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <ConfirmModal
          open={showClearModal}
          title="Clear Analytics Data"
          message="This will permanently delete all tracked analytics events. This cannot be undone."
          confirmLabel="Clear All Data"
          variant="danger"
          onConfirm={handleClear}
          onCancel={() => setShowClearModal(false)}
          loading={clearing}
        />
      </div>
    </GoogleSignInGate>
  );
}
